import React, {useCallback, useLayoutEffect, useRef, useState} from "react";
import {DataGridColumnTypes} from "../../../models";

/** Marker for the offscreen probe host. Live observers ignore everything inside it. */
export const HEIGHT_PROBE_ATTR = "data-grid-height-probe";

/** Marker for probe rows so layout averages skip them. */
export const HEIGHT_PROBE_ROW_ATTR = "data-grid-height-probe-row";

/** Column name stamped on each measured body cell. */
export const MEASURE_COLUMN_ATTR = "data-grid-measure-column";

const dataIdentityTokens = new WeakMap();
let dataIdentitySeq = 1;

/**
 * Stable identity for object signatures. Primitive values use their string form.
 * @param {unknown} value
 * @return {string}
 */
function identityToken(value) {
    if (value != null && (typeof value === "object" || typeof value === "function")) {
        let token = dataIdentityTokens.get(value);
        if (token == null) {
            token = dataIdentitySeq++;
            dataIdentityTokens.set(value, token);
        }
        return `r:${token}`;
    }
    return `v:${value == null ? "" : String(value)}`;
}

/**
 * Cell record before it is turned into the rendered cell array.
 * @param {object | null | undefined} row
 * @return {Record<string, unknown>}
 */
export function rawRowCells(row) {
    if (row?._cellsRaw && typeof row._cellsRaw === "object" && !Array.isArray(row._cellsRaw)) {
        return row._cellsRaw;
    }
    if (row?.cells && typeof row.cells === "object" && !Array.isArray(row.cells)) {
        return row.cells;
    }
    return {};
}

/**
 * Row shape the probe and the signature function both understand.
 * @param {object | null | undefined} row
 * @return {{key: *, data: *, cells: Record<string, unknown>}}
 */
export function toMeasureRow(row) {
    return {
        key: row?.key,
        data: row?.data,
        cells: rawRowCells(row),
    };
}

/**
 * @param {object | null | undefined} row
 * @param {object | null | undefined} column
 * @return {boolean}
 */
function isElementContent(row, column) {
    if (column?.type === DataGridColumnTypes.element) {
        return true;
    }
    const raw = rawRowCells(row)?.[column?.name];
    if (React.isValidElement(raw)) {
        return true;
    }
    return !!(raw != null && typeof raw === "object" && React.isValidElement(raw.value));
}

/**
 * Signature for invalidating a cached cell height.
 * Element cells follow the row data reference. Primitive cells follow their text.
 * @param {object | null | undefined} row
 * @param {object | null | undefined} column
 * @return {string}
 */
export function cellContentSignature(row, column) {
    const raw = rawRowCells(row)?.[column?.name];
    if (isElementContent(row, column)) {
        const source = row?.data != null ? row.data : raw;
        return `el:${identityToken(source)}`;
    }
    if (raw != null && typeof raw === "object" && !React.isValidElement(raw)) {
        const value = raw.value;
        return `p:${value == null ? "" : String(value)}`;
    }
    return `p:${raw == null ? "" : String(raw)}`;
}

/**
 * String, number, money, date, and boolean cells stay on the minimum row height.
 * Only element content is probed.
 * @param {object | null | undefined} column
 * @param {object | null | undefined} row
 * @return {boolean}
 */
export function columnNeedsHeightProbe(column, row) {
    if (!column?.name) {
        return false;
    }
    return isElementContent(row, column);
}

/**
 * Content height of a cell, ignoring the row's stretched height.
 * Adds the cell's own padding and border so the result matches the row it would produce alone.
 * @param {HTMLElement | null | undefined} td
 * @return {number}
 */
export function measureCellContribution(td) {
    if (!(td instanceof HTMLElement)) {
        return 0;
    }
    const inner = td.querySelector(":scope > .data-grid-row-body-inner-cell");
    if (!(inner instanceof HTMLElement)) {
        return Math.round(td.getBoundingClientRect().height || 0);
    }

    let contentHeight = 0;
    let hasElementChild = false;
    for (const child of inner.children) {
        if (!(child instanceof HTMLElement)) {
            continue;
        }
        hasElementChild = true;
        contentHeight = Math.max(contentHeight, child.getBoundingClientRect().height || 0);
    }

    // No element children means the inner box is just the stretched row. Keep the minimum
    // so an empty cell cannot hold the row open. Callers raise this to minRowHeight.
    if (!hasElementChild || contentHeight <= 0) {
        return 0;
    }

    const innerStyle = window.getComputedStyle(inner);
    contentHeight += (parseFloat(innerStyle.paddingTop) || 0) + (parseFloat(innerStyle.paddingBottom) || 0);

    const tdStyle = window.getComputedStyle(td);
    contentHeight += (parseFloat(tdStyle.paddingTop) || 0)
        + (parseFloat(tdStyle.paddingBottom) || 0)
        + (parseFloat(tdStyle.borderTopWidth) || 0)
        + (parseFloat(tdStyle.borderBottomWidth) || 0);
    return Math.round(contentHeight);
}

/**
 * @param {object} column
 * @param {Function} getColWidth
 * @return {number}
 */
export function columnWidthPx(column, getColWidth) {
    const width = typeof getColWidth === "function" ? getColWidth(column) : 0;
    return Math.round(width || 0);
}

/**
 * @param {{width: number, height: number, signature: string} | undefined} entry
 * @param {string} signature
 * @param {number} width
 * @return {boolean}
 */
function isHeightEntryFresh(entry, signature, width) {
    return !!entry && entry.signature === signature && entry.width === width && entry.height > 0;
}

/**
 * @param {Map<string, {height: number}>} rowMap
 * @param {number} minRowHeight
 * @return {number}
 */
function maxRowLock(rowMap, minRowHeight) {
    let max = minRowHeight;
    for (const entry of rowMap.values()) {
        if (entry.height > max) {
            max = entry.height;
        }
    }
    return max;
}

/**
 * Walks grouped rows so a nested key can be measured after it is expanded.
 * @param {object[] | undefined} rows
 * @param {string | number} key
 * @return {object | undefined}
 */
function findRowByKey(rows, key) {
    for (const row of rows ?? []) {
        if (row?.key != null && String(row.key) === String(key)) {
            return row;
        }
        const nested = findRowByKey(row?.groupedRows, key);
        if (nested) {
            return nested;
        }
    }
    return undefined;
}

/**
 * First window row that still has an element column missing from the cache.
 * @param {object[] | undefined} rows
 * @param {object[] | undefined} columns
 * @param {React.MutableRefObject<Map<*, Map<string, object>>>} cellCacheRef
 * @param {Function} getColWidth
 * @return {{row: object, columns: object[]} | null}
 */
export function findProbeRequest(rows, columns, cellCacheRef, getColWidth) {
    for (const source of rows ?? []) {
        const row = toMeasureRow(source);
        if (row.key == null) {
            continue;
        }
        const pending = [];
        for (const column of columns ?? []) {
            if (!columnNeedsHeightProbe(column, row)) {
                continue;
            }
            const width = columnWidthPx(column, getColWidth);
            if (width <= 0) {
                continue;
            }
            const signature = cellContentSignature(row, column);
            const entry = cellCacheRef.current.get(row.key)?.get(String(column.name));
            if (!isHeightEntryFresh(entry, signature, width)) {
                pending.push(column);
            }
        }
        if (pending.length) {
            return {row, columns: pending};
        }
    }
    return null;
}

/**
 * Includes expanded grouped rows in the probe window.
 * @param {object[] | undefined} rows
 * @param {object[]} [out]
 * @return {object[]}
 */
function flattenProbeRows(rows, out = []) {
    for (const row of rows ?? []) {
        if (row?.key != null) {
            out.push(row);
        }
        if (row?.groupedRows?.length) {
            flattenProbeRows(row.groupedRows, out);
        }
    }
    return out;
}

/**
 * Owns the per-cell height cache and the row lock (max cell height).
 * @param {object} args
 * @param {boolean} args.enabled
 * @param {string | undefined} args.density
 * @param {number} args.minRowHeight
 * @param {object[] | undefined} args.allRows
 * @param {object[] | undefined} args.sampleRows
 * @param {number} args.sampleLimit
 * @param {Function | undefined} args.onHeightSample
 * @param {Function} args.getColWidth
 * @param {object[] | undefined} args.columns
 */
export function useVirtualColumnHeightCache({
                                                 enabled,
                                                 density,
                                                 minRowHeight,
                                                 allRows,
                                                 sampleRows,
                                                 sampleLimit,
                                                 onHeightSample,
                                                 getColWidth,
                                                 columns,
                                             }) {
    const cellCacheRef = useRef(new Map());
    const rowLockRef = useRef(new Map());
    const columnsByNameRef = useRef(new Map());
    const getColWidthRef = useRef(getColWidth);
    const minRowHeightRef = useRef(minRowHeight);
    const [heightRevision, setHeightRevision] = useState(0);
    const [cacheEpoch, setCacheEpoch] = useState(0);
    const prevDensityRef = useRef(density);

    getColWidthRef.current = getColWidth;
    minRowHeightRef.current = minRowHeight;
    columnsByNameRef.current = new Map((columns ?? []).map((column) => [String(column.name), column]));

    if (prevDensityRef.current !== density) {
        prevDensityRef.current = density;
        cellCacheRef.current = new Map();
        rowLockRef.current = new Map();
    }

    /**
     * Writes measured cell heights and raises the row lock when the max changes.
     * A matching measurement leaves the lock untouched.
     * @type {(function(object, {columnName: string, height: number}[]): void)}
     */
    const commitMeasurements = useCallback((row, measurements) => {
        const rowKey = row?.key;
        if (rowKey == null || !measurements?.length) {
            return;
        }
        const minHeight = minRowHeightRef.current;
        let rowMap = cellCacheRef.current.get(rowKey);
        if (!rowMap) {
            rowMap = new Map();
            cellCacheRef.current.set(rowKey, rowMap);
        }
        let wrote = false;
        for (const measurement of measurements) {
            const columnName = measurement?.columnName;
            const column = columnsByNameRef.current.get(String(columnName));
            if (!columnName || !column || !columnNeedsHeightProbe(column, row)) {
                continue;
            }
            const width = columnWidthPx(column, getColWidthRef.current);
            if (width <= 0) {
                if (rowMap.has(String(columnName)) && rowMap.delete(String(columnName))) {
                    wrote = true;
                }
                continue;
            }
            const signature = cellContentSignature(row, column);
            const height = Math.max(minHeight, Math.round(measurement.height || 0));
            const prev = rowMap.get(String(columnName));
            if (prev && prev.signature === signature && prev.width === width && Math.abs(prev.height - height) <= 1) {
                continue;
            }
            rowMap.set(String(columnName), {width, height, signature});
            wrote = true;
        }
        if (!wrote) {
            return;
        }
        const nextLock = maxRowLock(rowMap, minHeight);
        const lockChanged = rowLockRef.current.get(rowKey) !== nextLock;
        if (lockChanged) {
            rowLockRef.current.set(rowKey, nextLock);
        }
        setCacheEpoch((epoch) => epoch + 1);
        if (lockChanged) {
            setHeightRevision((revision) => revision + 1);
        }
    }, []);

    /**
     * @type {(function(object): number | null)}
     */
    const getRowHeight = useCallback((row) => {
        const locked = rowLockRef.current.get(row?.key);
        return locked == null ? null : locked;
    }, []);

    /**
     * @type {(function(*): number | undefined)}
     */
    const getLockedRowHeight = useCallback((key) => {
        const locked = rowLockRef.current.get(key);
        return locked == null ? undefined : locked;
    }, []);

    /**
     * Publishes the average height of the first visible rows.
     * A row with no lock yet counts as the minimum, so one tall measurement cannot set the viewport.
     */
    useLayoutEffect(() => {
        if (!enabled || !onHeightSample) {
            return;
        }
        const limit = Math.max(1, sampleLimit || 1);
        const sample = (sampleRows ?? []).slice(0, limit);
        if (!sample.length) {
            return;
        }
        let sum = 0;
        for (const row of sample) {
            const locked = rowLockRef.current.get(row?.key);
            sum += locked == null ? minRowHeight : locked;
        }
        onHeightSample(sum / sample.length);
    }, [enabled, heightRevision, sampleRows, sampleLimit, onHeightSample, minRowHeight]);

    /**
     * Drops cache entries for rows that left the grid.
     */
    useLayoutEffect(() => {
        if (!enabled) {
            return;
        }
        const live = new Set();
        for (const row of allRows ?? []) {
            if (row?.key != null) {
                live.add(row.key);
            }
        }
        let changed = false;
        for (const key of cellCacheRef.current.keys()) {
            if (!live.has(key)) {
                cellCacheRef.current.delete(key);
                if (rowLockRef.current.delete(key)) {
                    changed = true;
                }
            }
        }
        if (changed) {
            setCacheEpoch((epoch) => epoch + 1);
            setHeightRevision((revision) => revision + 1);
        }
    }, [enabled, allRows]);

    return {
        heightRevision,
        cacheEpoch,
        cellCacheRef,
        commitMeasurements,
        getRowHeight,
        getLockedRowHeight,
    };
}

/**
 * Probes one unmeasured window row per frame and keeps a ResizeObserver on mounted cells.
 * @param {object} args
 * @param {boolean} args.enabled
 * @param {object[] | undefined} args.probeRows
 * @param {object[] | undefined} args.columns
 * @param {React.MutableRefObject<Map<*, Map<string, object>>>} args.cellCacheRef
 * @param {number} args.cacheEpoch
 * @param {Function} args.getColWidth
 * @param {Function} args.commitMeasurements
 * @param {React.RefObject<HTMLElement | null>} args.containerRef
 * @param {string | undefined} args.density
 * @param {string} args.mountedColumnKey
 * @return {{row: object, columns: object[]} | null}
 */
export function useVirtualColumnHeightProbe({
                                                 enabled,
                                                 probeRows,
                                                 columns,
                                                 cellCacheRef,
                                                 cacheEpoch,
                                                 getColWidth,
                                                 commitMeasurements,
                                                 containerRef,
                                                 density,
                                                 mountedColumnKey,
                                             }) {
    const [probeKey, setProbeKey] = useState(null);
    const probeRowsRef = useRef(probeRows);
    probeRowsRef.current = probeRows;

    const nextProbe = useMemoProbe(enabled, probeRows, columns, cellCacheRef, getColWidth, cacheEpoch, density);

    /**
     * Advances to the next unmeasured row on the following frame so the scan cannot
     * measure the whole window inside one layout pass.
     */
    React.useEffect(() => {
        if (!enabled) {
            setProbeKey(null);
            return undefined;
        }
        const nextKey = nextProbe?.row?.key ?? null;
        if (nextKey == null) {
            if (probeKey != null) {
                setProbeKey(null);
            }
            return undefined;
        }
        if (probeKey === nextKey) {
            return undefined;
        }
        const frame = window.requestAnimationFrame(() => setProbeKey(nextKey));
        return () => window.cancelAnimationFrame(frame);
    }, [enabled, nextProbe, probeKey]);

    const activeProbe = React.useMemo(() => {
        if (!enabled || probeKey == null) {
            return null;
        }
        const match = findRowByKey(probeRows, probeKey);
        return findProbeRequest(match ? [match] : [], columns, cellCacheRef, getColWidth);
    }, [enabled, probeKey, probeRows, columns, cellCacheRef, getColWidth, cacheEpoch, density]);

    /**
     * Records the content height of cells that are actually mounted.
     * Rebinds when the vertical window or the horizontal column window changes.
     */
    React.useEffect(() => {
        if (!enabled) {
            return undefined;
        }
        const root = containerRef.current;
        if (!root) {
            return undefined;
        }
        const observer = new ResizeObserver((entries) => {
            /** @type {Map<string, {columnName: string, height: number}[]>} */
            const byRowKey = new Map();
            for (const entry of entries) {
                const target = entry.target;
                if (!(target instanceof HTMLElement)) {
                    continue;
                }
                if (target.closest(`[${HEIGHT_PROBE_ATTR}]`)) {
                    continue;
                }
                const td = target.closest(`td[${MEASURE_COLUMN_ATTR}]`);
                const rowEl = target.closest("tr[data-grid-row-key]");
                if (!(td instanceof HTMLElement) || !(rowEl instanceof HTMLElement)) {
                    continue;
                }
                const rowKey = rowEl.getAttribute("data-grid-row-key");
                const columnName = td.getAttribute(MEASURE_COLUMN_ATTR);
                if (!rowKey || !columnName) {
                    continue;
                }
                const list = byRowKey.get(rowKey) ?? [];
                list.push({columnName, height: measureCellContribution(td)});
                byRowKey.set(rowKey, list);
            }
            byRowKey.forEach((measurements, rowKey) => {
                const source = findRowByKey(probeRowsRef.current, rowKey);
                if (!source) {
                    return;
                }
                commitMeasurements(toMeasureRow(source), measurements);
            });
        });
        root.querySelectorAll(`td[${MEASURE_COLUMN_ATTR}]`).forEach((td) => {
            if (!(td instanceof HTMLElement) || td.closest(`[${HEIGHT_PROBE_ATTR}]`)) {
                return;
            }
            const inner = td.querySelector(":scope > .data-grid-row-body-inner-cell");
            observer.observe(inner instanceof HTMLElement ? inner : td);
        });
        return () => observer.disconnect();
    }, [enabled, containerRef, commitMeasurements, mountedColumnKey, density, probeRows]);

    return activeProbe;
}

/**
 * Memoizes the next probe target. Split out so the dependency list stays next to the cache epoch.
 * @param {boolean} enabled
 * @param {object[] | undefined} probeRows
 * @param {object[] | undefined} columns
 * @param {React.MutableRefObject<Map<*, Map<string, object>>>} cellCacheRef
 * @param {Function} getColWidth
 * @param {number} cacheEpoch
 * @param {string | undefined} density
 * @return {{row: object, columns: object[]} | null}
 */
function useMemoProbe(enabled, probeRows, columns, cellCacheRef, getColWidth, cacheEpoch, density) {
    return React.useMemo(() => {
        if (!enabled) {
            return null;
        }
        return findProbeRequest(flattenProbeRows(probeRows), columns, cellCacheRef, getColWidth);
    }, [enabled, probeRows, columns, cellCacheRef, getColWidth, cacheEpoch, density]);
}
